// ============================================================================
// client/cascade-papier.ts — LE RIDEAU DES CASCADES (eau et lave)
// ----------------------------------------------------------------------------
// Extrait de `drawFrontDecos` (client/render.ts) le 31/07, pour une raison précise : Claude-Alain
// veut comparer trois façons de MIEUX CACHER ce qui passe derrière, et une planche qui repeindrait
// la cascade « à peu près » ferait valider autre chose que ce que le jeu affiche. Le jeu et la
// planche appellent donc la même fonction, comme pour les compagnons.
//
// ⚠️ LA LOI N° 2 DE L'AUDIT DU 28/07 EST LA CONTRAINTE DE FOND. Une carte de la famille portait
// SOIXANTE-CINQ cascades, dessinées par-dessus le jeu et plus visibles que le héros : l'œil devait
// trier. D'où le plafond à 42 % d'opacité. Toute variante qui cache mieux marche donc sur cette
// ligne — mieux cacher, c'est par construction se voir davantage. Les trois propositions ne
// répondent pas par la même mécanique :
//   0 · BANDES   — l'existant : quatre bandes fines, très ajourées.
//   1 · RIDEAU   — même opacité, mais la nappe couvre TOUTE la case. On cache par la COUVERTURE.
//   2 · VOILE    — un voile faible sur toute la case + les bandes par-dessus. Deux couches.
//   3 · DÉGRADÉ  — dense en haut, clair en bas. On cache LÀ OÙ ÇA NE GÊNE PAS : le joueur marche
//                  en bas de case, et c'est là qu'il faut rester lisible.
// ============================================================================

import { rgba } from './papier'

type Ctx = CanvasRenderingContext2D

export const VARIANTES_CASCADE = [
  { n: 0, nom: 'Bandes', desc: 'L’existant : quatre bandes fines, très ajourées.' },
  { n: 1, nom: 'Rideau', desc: 'La nappe couvre toute la case. On cache par la couverture, pas par l’opacité.' },
  { n: 2, nom: 'Voile', desc: 'Un voile faible sur toute la case, et les bandes par-dessus. Deux couches.' },
  { n: 3, nom: 'Dégradé', desc: 'Dense en haut, clair en bas — on garde lisible la rangée où l’on marche.' },
  // ── SECOND TOUR (31/07) : « il me faut encore 3 maquettes qui rendent ENCORE plus opaque ».
  // Trois mécaniques différentes, pas trois fois la même montée d'opacité — sinon le choix se
  // réduit à un curseur, et autant en donner un seul.
  { n: 4, nom: 'Mur d’eau', desc: 'Le maximum assumé : nappe pleine, opacité poussée bien au-delà du plafond. On ne voit plus rien derrière, et la cascade se voit beaucoup.' },
  { n: 5, nom: 'Dégradé profond', desc: 'Ton choix actuel, poussé : la descente court sur cinq cases et ne s’éclaircit qu’à moitié. Ça cache AUSSI en bas de chute, là où le dégradé actuel lâche.' },
  { n: 6, nom: 'Double nappe', desc: 'Deux nappes qui glissent à des vitesses différentes, plus un embrun. On cache par la MATIÈRE qui se croise, pas en assombrissant : la silhouette derrière se brise au lieu de s’assombrir.' },
] as const

/**
 * LE CHOIX DE CLAUDE-ALAIN, second tour (31/07) : **DÉGRADÉ PROFOND**.
 *
 * Il avait retenu DÉGRADÉ le matin, puis l'a trouvé encore trop transparent. Celui-ci garde la
 * même mécanique — dense au déversoir, plus clair en bas — mais la descente court sur cinq cases
 * et surtout ne s'éclaircit qu'à moitié (plancher 0,45 au lieu de 0,18).
 *
 * ⚠️ Il dépasse le plafond de 42 % de la loi n° 2 SUR TOUTE LA CHUTE, pas seulement en tête comme
 * le précédent. Le risque nommé sur la planche reste le même, en plus marqué : sur une carte qui
 * porte beaucoup de cascades, l'écran se charge. C'est un choix assumé, pas un oubli.
 */
export const CASCADE_CHOISIE = 5

const TONS_EAU = ['#4a9ed0', '#6fb0d8', '#a8d8f0']
const TONS_LAVE = ['#e8511e', '#ff7a2e', '#ffa04a']

export interface OptsCascade {
  lave: boolean
  /** Dernière case de la colonne : le pied s'évase. */
  bas: boolean
  /** Première case de la colonne : un pli plus clair marque le déversoir. */
  sommet: boolean
  /** Position d'écoulement, déjà multipliée par la vitesse de la matière. */
  flow: number
  /** Opacité de base — le plafond de la loi n° 2, atténué si la case est immergée. */
  alpha: number
  /** Rang de la case DEPUIS LE SOMMET de sa colonne (0 = le déversoir).
   *  ⚠️ Indispensable au dégradé : calculé par case, il se répétait à CHAQUE case et une colonne
   *  de quatre affichait quatre bandes dense/clair au lieu d'une seule descente. */
  depuisSommet: number
  variante?: number
}

/**
 * Peint UNE case de cascade en (x, y), côté `TS`.
 * L'appelant a déjà posé son propre `save()` ; la fonction gère son alpha et le restaure.
 */
export function peindreCascade(ctx: Ctx, x: number, y: number, TS: number, o: OptsCascade): void {
  const tons = o.lave ? TONS_LAVE : TONS_EAU
  const v = o.variante ?? CASCADE_CHOISIE
  ctx.save()

  // ── LE FOND, propre à chaque variante ──────────────────────────────────────────────────
  if (v === 1) {
    // RIDEAU : une nappe pleine, striée de teintes voisines. Même opacité que les bandes, mais
    // plus rien ne passe entre elles — c'est la COUVERTURE qui cache, pas la densité.
    ctx.globalAlpha = o.alpha
    ctx.fillStyle = tons[0]!
    ctx.fillRect(x, y, TS, TS)
    for (let k = 0; k < 6; k++) {
      ctx.globalAlpha = o.alpha * (0.5 + (k % 2) * 0.45)
      ctx.fillStyle = tons[1 + (k % 2)]!
      ctx.fillRect(x + k * TS / 6, y, TS / 6 - 1, TS)
    }
  } else if (v === 2) {
    // VOILE : une couche faible partout, qui brouille sans masquer, puis les bandes d'origine
    // par-dessus. Ce qui passe derrière reste deviné, mais jamais net.
    ctx.globalAlpha = o.alpha * 0.55
    ctx.fillStyle = tons[0]!
    ctx.fillRect(x, y, TS, TS)
  } else if (v === 3) {
    // DÉGRADÉ : dense au DÉVERSOIR, clair en bas de chute. En haut il n'y a que du décor à
    // cacher ; en bas, c'est la rangée où le joueur marche et où il faut rester lisible.
    // ⚠️ La descente court sur TOUTE LA COLONNE (via `depuisSommet`), pas sur la case. Calculée
    // par case, elle se répétait à chaque case et donnait quatre bandes au lieu d'une chute.
    const DESCENTE = 3                                  // en cases : au-delà, c'est clair
    const t0 = Math.min(1, o.depuisSommet / DESCENTE)
    const t1 = Math.min(1, (o.depuisSommet + 1) / DESCENTE)
    const dens = (t: number): number => 1 - t * 0.82
    const gr = ctx.createLinearGradient(x, y, x, y + TS)
    gr.addColorStop(0, rgba(tons[0]!, dens(t0)))
    gr.addColorStop(1, rgba(tons[1]!, dens(t1)))
    ctx.globalAlpha = Math.min(1, o.alpha * 1.7)        // ⚠️ AU-DESSUS du plafond, en tête de chute
    ctx.fillStyle = gr
    ctx.fillRect(x, y, TS, TS)
  } else if (v === 4) {
    // MUR D'EAU : la réponse la plus directe à « plus opaque ». Nappe pleine, opacité poussée à
    // plus du double du plafond, uniforme du haut en bas de la colonne. Rien ne passe.
    // ⚠️ C'est aussi celle qui contredit le plus la loi n° 2 : sur la carte aux 65 cascades, elle
    // repeindra 65 cases en bleu franc. Le prix est nommé, c'est à Claude-Alain de le payer ou non.
    ctx.globalAlpha = Math.min(0.94, o.alpha * 2.15)
    ctx.fillStyle = tons[0]!
    ctx.fillRect(x, y, TS, TS)
    for (let k = 0; k < 5; k++) {                       // des stries larges, sinon c'est un aplat mort
      ctx.globalAlpha = Math.min(0.94, o.alpha * 2.15) * (0.62 + (k % 2) * 0.38)
      ctx.fillStyle = tons[1 + (k % 2)]!
      ctx.fillRect(x + k * TS / 5, y, TS / 5 - 1, TS)
    }
  } else if (v === 5) {
    // DÉGRADÉ PROFOND : la mécanique retenue le 31/07, mais qui ne lâche plus en bas. Deux
    // changements, et deux seulement — la descente s'étale sur cinq cases au lieu de trois, et
    // surtout le PLANCHER remonte : on ne s'éclaircit qu'à moitié (0,45) au lieu de presque rien
    // (0,18). C'est ce plancher qui fait la différence, pas la longueur.
    const DESCENTE = 5
    const t0 = Math.min(1, o.depuisSommet / DESCENTE)
    const t1 = Math.min(1, (o.depuisSommet + 1) / DESCENTE)
    const dens = (t: number): number => 1 - t * 0.45
    const gr = ctx.createLinearGradient(x, y, x, y + TS)
    gr.addColorStop(0, rgba(tons[0]!, dens(t0)))
    gr.addColorStop(1, rgba(tons[1]!, dens(t1)))
    ctx.globalAlpha = Math.min(1, o.alpha * 1.95)
    ctx.fillStyle = gr
    ctx.fillRect(x, y, TS, TS)
  } else if (v === 6) {
    // DOUBLE NAPPE : cacher par la MATIÈRE, pas par le noir. Deux nappes de largeurs et de
    // vitesses différentes se croisent, et un embrun de grains clairs passe par-dessus. Une
    // silhouette derrière ne s'assombrit pas, elle se BRISE — et c'est la silhouette qui trahit
    // ce qui passe, bien plus que la luminosité.
    // ⚠️ L'embrun est fait de grains de PAPIER (petits carrés durs), pas de dômes d'écume : les
    // dômes étaient le langage cartoon de juillet, écarté avec le reste de cette feuille.
    // ⚠️ Réglages remontés après la première capture : à 1,55 le héros restait NET derrière la
    // nappe. Une variante demandée « encore plus opaque » qui cache autant que l'existant ne sert
    // à rien. Elle reste malgré tout la plus légère des trois — c'est son intérêt.
    const a = Math.min(0.92, o.alpha * 1.85)
    ctx.globalAlpha = a * 0.85
    ctx.fillStyle = tons[0]!
    ctx.fillRect(x, y, TS, TS)
    for (const [larg, vit, ton] of [[5, 1, 1], [7, -0.55, 2]] as const) {
      const per = TS / larg
      const off = ((o.flow * vit) % per + per) % per
      ctx.globalAlpha = a * (ton === 1 ? 0.75 : 0.58)
      ctx.fillStyle = tons[ton]!
      for (let bx = x - per + off; bx < x + TS; bx += per * 2) {
        const gx = Math.max(x, bx), gw = Math.min(x + TS, bx + per) - gx
        if (gw > 0) ctx.fillRect(gx, y, gw, TS)
      }
    }
    ctx.globalAlpha = a * 0.5                            // l'embrun : des grains qui descendent
    ctx.fillStyle = '#ffffff'
    for (let k = 0; k < 9; k++) {
      const gx = x + ((k * 37) % TS)
      const gy = y + (((o.flow * 1.9 + k * 23) % TS) + TS) % TS
      ctx.fillRect(gx, gy, 2, 2)
    }
  }

  // ── LES BANDES : seules les variantes AJOURÉES en ont. Les nappes pleines (rideau, mur,
  //    double nappe) couvrent déjà toute la case ; les rajouter ne ferait qu'assombrir. ────────
  if (v !== 1 && v !== 4 && v !== 6) {
    ctx.globalAlpha = o.alpha
    for (let k = 0; k < 4; k++) {
      const ev = o.bas ? 4 : 0
      const bx = x + 7 + k * (TS - 16) / 3.4
      const w = TS * (0.1 + (k % 2) * 0.03)
      ctx.fillStyle = tons[k % 3] ?? tons[0]!
      ctx.beginPath()
      ctx.moveTo(bx, y); ctx.lineTo(bx + w, y)
      ctx.lineTo(bx + w + ev * (k - 1.5) * .6, y + TS); ctx.lineTo(bx + ev * (k - 1.5) * .6, y + TS)
      ctx.closePath(); ctx.fill()
    }
  }

  // ── LES PLIS QUI DESCENDENT : eux seuls donnent le mouvement, dans toutes les variantes ──
  // `pleine` = la variante couvre déjà toute la case, donc les plis s'y répartissent sur toute la
  // largeur au lieu de suivre les quatre bandes. Sans ça, mur et double nappe portaient leurs plis
  // au milieu et laissaient les bords immobiles : la nappe avait l'air d'un aplat collé.
  const pleine = v === 1 || v === 4 || v === 6
  ctx.globalAlpha = o.alpha
  ctx.fillStyle = rgba('#ffffff', pleine ? .22 : .3)
  const nb = pleine ? 6 : 4
  for (let k = 0; k < nb; k++) {
    const bx = pleine ? x + k * TS / 6 + 2 : x + 7 + k * (TS - 16) / 3.4
    const w = pleine ? TS / 6 - 5 : TS * (0.1 + (k % 2) * 0.03)
    const per = 30, off = ((o.flow + k * 9) % per + per) % per
    for (let yy = y - per + off; yy < y + TS; yy += per) {
      const h = Math.max(0, Math.min(y + TS, yy + 7) - Math.max(y, yy))
      if (h > 0) ctx.fillRect(bx + 1, Math.max(y, yy), Math.max(1, w - 2), h)
    }
  }

  // ── LE DÉVERSOIR : le seul repère du sommet de colonne ──────────────────────────────────
  if (o.sommet) {
    ctx.globalAlpha = o.alpha
    ctx.fillStyle = rgba(o.lave ? '#ffd24a' : '#e8f6fc', .55)
    ctx.fillRect(x + 6, y, TS - 12, 3)
  }
  ctx.restore()
}
